import { Component, Host, Prop, Method, Event, EventEmitter, h, Element, Watch } from '@stencil/core';
import { createPopper } from '@popperjs/core';
import { floatingElements } from '../../utils/floating-elements.utils';
import { Animation } from '@ionic/core';
import { tooltipOpenAnimation } from './animations/open';
import { tooltipCloseAnimation } from './animations/close';
import { DISABLE_ANIMATION_VALUE } from '../../utils/animations';
import { ElementOrSelector } from '../../utils/types/common-props.types';

export declare type Placement = 'top' | 'bottom' | 'right' | 'left' | 'auto';

const CURSOR_ANCHOR_NAME = 'cursor';

export declare type VirtualElement = {
  getBoundingClientRect: () => DOMRect;
  contextElement?: Element;
};

/**
 * @slot - Designed to add custom content inside the tooltip.
 */
@Component({
  tag: 'sy-tooltip',
  styleUrls: ['scss/index.scss', 'scss/_tokens.scss'],
  shadow: true,
})
export class Tooltip {
  private cursorPosition: {
    x: number;
    y: number;
  } | null;

  private currentAnimation: Animation | undefined;

  /**
   * Popper instance.
   */
  private popper: any;

  /**
   * Element that will anchor the tooltip
   * Virtual element that allows to position the popper relative to a virtual area defined by any coordinates
   */
  private anchor: Element | VirtualElement | null;

  /**
   * Element that will trigger the tooltip
   */
  private triggerElement: Element | null;

  /**
   * ID of the last timeout
   */
  private delayTimeoutId: any;

  /**
   * Reference to function that will show the tooltip
   */
  private showHandlerFunc: any;

  /**
   * Reference to function that will hide the tooltip
   */
  private hideHandlerFunc: any;

  /**
   * Reference to function that will toggle the tooltip
   */
  private toggleHandlerFunc: any;

  /**
   * Reference to function that will track the cursor and touch movement
   */
  private mouseHandlerFunc: any;

  /**
   * Array with handlers that will make the tooltip visible
   */
  private showHandlersArr: Array<string>;

  /**
   * Array with handlers that will make the tooltip hidden
   */
  private hideHandlersArr: Array<string>;

  /**
   * Array with handlers that are being used both to hide and show the tooltip.
   */
  private toggleHandlersArr: Array<string>;

  @Element() el: HTMLSyTooltipElement;

  /**
   * Sets tooltip's type.
   * If 'default', it sets the host element style to the default tooltip style.
   * If 'none', the host element won't have style.
   */
  @Prop({ reflect: true }) tooltipStyle: 'default' | 'none' = 'default';

  /**
   * Optional custom animation to replace the default and be played when the component starts opening.
   * If 'none', the tooltip won't have open animation.
   */
  @Prop() openAnimation?: ((element: HTMLElement) => Animation) | string;

  /**
   * Optional custom animation to replace the default and be played when the component starts closing.
   * If 'none', the tooltip won't have close animation.
   */
  @Prop() closeAnimation?: ((element: HTMLElement) => Animation) | string;

  /**
   * Disables all animations
   */
  @Prop() disableAnimations = false;

  /**
   * Text displayed in tooltip.
   */
  @Prop() label: string;

  /**
   * Position of the tooltip in relation to the anchor element.
   * If there is no space for such placement, it will use the closest one available.
   * If set to "auto", defines automatically the position, based on the available space.
   */
  @Prop({ reflect: true }) placement: Placement = 'auto';

  /**
   * Sets the delay in ms to show / hide the tooltip. It is possible to define differents values to each action.
   */
  @Prop({ reflect: true }) delay: number | { show: number; hide: number } = 100;

  /**
   * Element or selector of the element that will be the anchor of the tooltip
   * If it is not set, the parent element will be the anchor.
   */
  @Prop({ reflect: true }) for?: ElementOrSelector | 'cursor' | undefined;

  /**
   * Element or selector of the element that will trigger the tooltip
   * If it is not set, the anchor will be trigger.
   * If it is not set and the anchor is set to 'cursor', the trigger will be the parent element
   */

  @Prop() trigger?: ElementOrSelector | undefined;

  /**
   * List of handlers that will make the tooltip visible. Use ',' to declare multiple handlers.
   */
  @Prop({ reflect: true }) showHandlers = 'mouseenter';

  /**
   * List of handlers that will hide the tooltip. Use ',' to declare multiple handlers.
   */
  @Prop({ reflect: true }) hideHandlers = 'mouseleave';

  @Watch('for')
  @Watch('trigger')
  @Watch('showHandlers')
  @Watch('hideHandlers')
  forHandlerWatcher(): void {
    this.removeCursorHandler();
    this.removeAnchorHandler();
    this.setAnchorAndTrigger();
  }

  /**
   * If true, the tooltip is visible
   */
  @Prop({ mutable: true, reflect: true }) visible = false;

  /**
   * Emitted after tooltip has become visible.
   */
  @Event() syTooltipDidOpen: EventEmitter<any>;

  /**
   * Emitted after tooltip has become hidden.
   */
  @Event() syTooltipDidClose: EventEmitter<any>;

  /**
   * Emitted when a tooltip starts to hide.
   */
  @Event() syTooltipWillHide: EventEmitter<void>;

  /**
   * Emitted when a tooltip starts to show.
   */
  @Event() syTooltipWillShow: EventEmitter<void>;

  componentDidRender() {
    if (this.visible) {
      this.popper?.update();
    }
  }

  componentDidLoad() {
    this.setAnchorAndTrigger();
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;
    this.removeCursorHandler();
    this.removeAnchorHandler();
  }

  /**
   * Method responsible for making the tooltip visible.
   * If 'instant' is true, the tooltip won't have open animation.
   * @param delay Number of milliseconds to wait before changing the visibility.
   */
  @Method()
  async show(instant = false): Promise<void> {
    // Adds cursor handler before showing the tooltip
    if (!this.cursorPosition && this.triggerElement && this.for === CURSOR_ANCHOR_NAME) {
      this.addCursorHandler();
      return;
    }
    clearTimeout(this.delayTimeoutId);
    this.delayTimeoutId = setTimeout(
      async () => {
        if (this.visible || !this.anchor || !this.triggerElement) return;

        this.syTooltipWillShow.emit();
        this.visible = true;
        await floatingElements.open(this.el);

        this.popper = createPopper(this.anchor, this.el, this.getPopperConfig());

        this.syTooltipDidOpen.emit();

        if (this.currentAnimation) {
          this.currentAnimation.stop();
        }

        if (((typeof instant === 'boolean' && !instant) || typeof instant !== 'boolean') && !this.disableAnimations) {
          this.currentAnimation = this.getOpenAnimation();
          if (this.currentAnimation) {
            await this.currentAnimation.play();
          }
        }
      },
      typeof this.delay === 'object' ? this.delay.show : this.delay,
    );
  }

  /**
   * Method responsible for making the tooltip hidden.
   * If 'instant' is true, the tooltip won't have close animation.
   * @param delay Number of milliseconds to wait before changing the visibility.
   */
  @Method()
  async hide(instant = false): Promise<void> {
    // Removes cursor handler before hiding the tooltip
    this.removeCursorHandler();
    clearTimeout(this.delayTimeoutId);
    this.delayTimeoutId = setTimeout(
      async () => {
        if (!this.visible) return;

        if (this.currentAnimation) {
          this.currentAnimation.stop();
        }

        this.syTooltipWillHide.emit();

        if (((typeof instant === 'boolean' && !instant) || typeof instant !== 'boolean') && !this.disableAnimations) {
          this.currentAnimation = this.getCloseAnimation();
          if (this.currentAnimation) {
            await this.currentAnimation.play();
          }
        }

        if (this.popper) {
          this.popper.destroy();
        }
        this.visible = false;
        floatingElements.close(this.el);
        this.syTooltipDidClose.emit();
      },
      typeof this.delay === 'object' ? this.delay.hide : this.delay,
    );
  }

  /**
   * Responsible for making the tooltip visible if it is hidden and hide it if it is visible.
   */
  @Method()
  async toggle(instant = false): Promise<void> {
    if (this.visible) {
      this.hideHandlerFunc(instant);
    } else {
      this.showHandlerFunc(instant);
    }
  }

  private generateGetBoundingClientRect(x = 0, y = 0): any {
    return () => ({
      width: 0,
      height: 0,
      top: y,
      right: x,
      bottom: y,
      left: x,
    });
  }

  private getOpenAnimation() {
    if (typeof this.openAnimation === 'string') {
      return this.openAnimation === DISABLE_ANIMATION_VALUE ? undefined : tooltipOpenAnimation(this.el);
    }
    const animationBuilder = this.openAnimation || tooltipOpenAnimation;
    return animationBuilder(this.el);
  }

  private getCloseAnimation() {
    if (typeof this.closeAnimation === 'string') {
      return this.closeAnimation === DISABLE_ANIMATION_VALUE ? undefined : tooltipCloseAnimation(this.el);
    }
    const animationBuilder = this.closeAnimation || tooltipCloseAnimation;
    return animationBuilder(this.el);
  }

  private setAnchorAndTrigger() {
    try {
      if (typeof this.for === 'string') {
        this.anchor =
          this.for === CURSOR_ANCHOR_NAME
            ? {
                getBoundingClientRect: this.generateGetBoundingClientRect(),
                contextElement: undefined,
              }
            : (this.el.getRootNode() as Element).querySelector(this.for) || this.el.parentElement;
      } else {
        this.anchor = this.for || this.el.parentElement;
      }
    } catch (err) {
      console.error(`Error fetching anchor element - ${err}`, this.el);
    }

    this.setTrigger();

    if (this.triggerElement) {
      this.addAnchorHandler();
    }
  }

  private setTrigger(): void {
    try {
      if (this.for === CURSOR_ANCHOR_NAME) {
        this.setTriggerElement(this.el.parentElement);
      } else {
        this.setTriggerElement(this.anchor);
      }
    } catch (err) {
      console.error(`Error fetching trigger element - ${err}`, this.el);
    }
    if (this.anchor && this.for === CURSOR_ANCHOR_NAME) {
      (this.anchor as VirtualElement).contextElement = this.triggerElement as Element;
    }
  }

  private setTriggerElement(elseReturn: any) {
    if (typeof this.trigger === 'string') {
      this.triggerElement = (this.el.getRootNode() as Element).querySelector(this.trigger) || elseReturn;
    } else {
      this.triggerElement = this.trigger || elseReturn;
    }
  }

  private addAnchorHandler() {
    this.showHandlerFunc = this.show.bind(this);
    this.hideHandlerFunc = this.hide.bind(this);
    this.toggleHandlerFunc = this.toggle.bind(this);

    this.showHandlersArr = this.showHandlers.split(',').map((str) => str.trim());
    this.hideHandlersArr = this.hideHandlers.split(',').map((str) => str.trim());

    // Handlers that are both being used in show and hide, will be treated as toggle.
    this.toggleHandlersArr = this.showHandlersArr.filter((handler) => this.hideHandlersArr.includes(handler));
    this.showHandlersArr = this.showHandlersArr.filter((handler) => !this.toggleHandlersArr.includes(handler));
    this.hideHandlersArr = this.hideHandlersArr.filter((handler) => !this.toggleHandlersArr.includes(handler));

    this.showHandlersArr.forEach((handler) => {
      this.triggerElement?.addEventListener(handler, this.showHandlerFunc);
    });

    this.hideHandlersArr.forEach((handler) => {
      this.triggerElement?.addEventListener(handler, this.hideHandlerFunc);
    });

    this.toggleHandlersArr.forEach((handler) => {
      this.triggerElement?.addEventListener(handler, this.toggleHandlerFunc);
    });
  }

  private removeAnchorHandler() {
    clearTimeout(this.delayTimeoutId);

    if (this.triggerElement) {
      this.showHandlersArr.forEach((handler) => {
        this.triggerElement?.removeEventListener(handler, this.showHandlerFunc);
      });

      this.hideHandlersArr.forEach((handler) => {
        this.triggerElement?.removeEventListener(handler, this.hideHandlerFunc);
      });

      this.toggleHandlersArr.forEach((handler) => {
        this.triggerElement?.removeEventListener(handler, this.toggleHandlerFunc);
      });
    }
  }

  private addCursorHandler() {
    this.mouseHandlerFunc = this.updateTooltipPosition.bind(this);
    this.triggerElement?.addEventListener('mousemove', this.mouseHandlerFunc);
    this.triggerElement?.addEventListener('touchmove', this.mouseHandlerFunc);
  }

  private removeCursorHandler() {
    this.cursorPosition = null;
    this.triggerElement?.removeEventListener('mousemove', this.mouseHandlerFunc);
    this.triggerElement?.removeEventListener('touchmove', this.mouseHandlerFunc);
  }

  private updateTooltipPosition({ clientX: x, clientY: y }) {
    if (!this.cursorPosition) {
      this.cursorPosition = { x, y };
      if (this.anchor) {
        this.anchor.getBoundingClientRect = this.generateGetBoundingClientRect(x, y);
      }
      this.show();
      return;
    }
    if (!this.anchor) {
      return;
    }
    this.anchor.getBoundingClientRect = this.generateGetBoundingClientRect(x, y);
    this.popper?.update();
  }

  private getTooltipAttributes() {
    const attributes: any = {
      role: 'tooltip',
    };
    return attributes;
  }

  private getPopperConfig(): any {
    const defaultPopperConfig: any = {
      placement: this.placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
      ],
    };

    if (this.for !== CURSOR_ANCHOR_NAME) {
      defaultPopperConfig.strategy = 'fixed';
    }

    return {
      ...defaultPopperConfig,
    };
  }

  render() {
    return (
      <Host {...this.getTooltipAttributes()}>
        {this.label}
        <slot></slot>
      </Host>
    );
  }
}
